import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import { ErrorState } from '@/shared/components/ErrorState';
import { formatDate } from '@/shared/lib/format';
import type { WhatChanged } from '../api/schemas';
import { useBriefQuery, useWhatChangedQuery } from '../hooks/useDocumentQuery';
import { ChangeCard } from './ChangeCard';
import { PageCite } from './PageCite';

type Props = { documentId: string; series: string; onOpenPage: (page: number) => void };

const counts = (data: WhatChanged) => {
  const moved = data.changes.filter((c) => c.previous !== null && c.current !== null);
  return { up: moved.filter((c) => (c.current ?? 0) > (c.previous ?? 0)).length, down: moved.filter((c) => (c.current ?? 0) < (c.previous ?? 0)).length };
};

function PreviousIssue({ previous, compared }: { previous: WhatChanged['previous']; compared: boolean }) {
  if (!previous) return null;
  return (
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {compared ? 'Compared with ' : 'Previous issue: '}
      <Link component={RouterLink} to={`/documents/${previous.id}`}>{previous.title}</Link>, {formatDate(previous.publishDate)}.
      {compared ? ' Rounding is not counted.' : ''}
    </Typography>
  );
}

/** The forecasts this report revised against the previous issue of its series, one card each. */
export function WhatChangedTab({ documentId, series, onOpenPage }: Props) {
  const { data, isPending, isError, error, refetch } = useWhatChangedQuery(documentId, true);
  const reason = useBriefQuery(documentId).data?.changeReason;

  if (isPending) return <Stack sx={{ gap: 1, p: 2 }}>{[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={76} />)}</Stack>;
  if (isError) return <Box sx={{ p: 2 }}><ErrorState error={error} onRetry={() => void refetch()} /></Box>;
  if (!data.hasForecasts) {
    return (
      <Stack sx={{ gap: 1.5, p: 2 }}>
        <Alert severity="info" variant="outlined">This report has no forecast table, so there are no forecast revisions to compare.</Alert>
        <PreviousIssue previous={data.previous} compared={false} />
      </Stack>
    );
  }
  if (!data.previous) return <Box sx={{ p: 2 }}><Alert severity="info" variant="outlined">This is the first report in its series, so there is nothing to compare.</Alert></Box>;

  const { up, down } = counts(data);
  return (
    <Stack sx={{ gap: 1.25, p: 2 }}>
      <Typography variant="body2">
        <Box component="strong" sx={{ fontWeight: 600 }}>{data.changes.length === 0 ? 'No forecasts revised' : `${data.changes.length} forecast${data.changes.length === 1 ? '' : 's'} revised`}</Box>
        {` vs the previous ${series}`}{data.changes.length ? `, ${up} up and ${down} down.` : '.'}
      </Typography>
      {reason ? (
        <Typography variant="body2" sx={{ p: 1.25, borderRadius: 1, bgcolor: 'surface.subtle' }}>
          Why, in the report's words: {reason.text} <PageCite page={reason.page} onOpen={onOpenPage} />
        </Typography>
      ) : null}
      {data.changes.map((c, i) => <ChangeCard key={`${c.instrument}-${c.periodLabel}`} change={c} index={i} onOpenPage={onOpenPage} />)}
      <PreviousIssue previous={data.previous} compared />
    </Stack>
  );
}
